/**
 * Shared money/number formatting driven by the organization's commercial settings.
 *
 * The number format (decimal + thousands separators) is configured once in
 * Settings → Organization defaults and applied app-wide. It is held in module
 * state (set by PropertyContext when effective settings load) so the many small
 * `money()` helpers across pages don't each need React context.
 */
let numberFormat = { decimalSeparator: '.', thousandsSeparator: ',' };

export function setNumberFormat(fmt) {
  if (!fmt) return;
  numberFormat = {
    decimalSeparator: fmt.decimalSeparator || '.',
    thousandsSeparator: fmt.thousandsSeparator ?? ',',
  };
}

export function getNumberFormat() {
  return numberFormat;
}

/** Formats a plain number with the configured separators, e.g. 1234567.5 → "1,234,567.50". */
export function formatAmount(value, decimals = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return formatAmount(0, decimals);
  const sign = n < 0 ? '-' : '';
  const [intPart, fracPart] = Math.abs(n).toFixed(decimals).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u0001')
    .split('\u0001')
    .join(numberFormat.thousandsSeparator);
  return fracPart != null && fracPart.length > 0
    ? `${sign}${grouped}${numberFormat.decimalSeparator}${fracPart}`
    : `${sign}${grouped}`;
}

/** Formats a `{ currency, amount }` object, e.g. → "IDR 1,250,000.00" ("—" when absent). */
export function money(m) {
  return m ? `${m.currency} ${formatAmount(m.amount)}` : '—';
}

/** Formats a currency code + numeric amount, e.g. amount('IDR', 1250000) → "IDR 1,250,000.00". */
export function amount(currency, value, decimals = 2) {
  return `${currency ? `${currency} ` : ''}${formatAmount(value, decimals)}`.trim();
}
